/**
 * Consolidated Auth Store with Full RBAC Support
 *
 * This store manages authentication state and RBAC permissions for the frontend.
 * It supports multi-tenant client switching, permission checking, and role management.
 */

import { createSignal } from "solid-js";
import type { Client, UserWithRoles, RoleAssignment, ClientAssignment } from "@shared/types/rbac";
import { analytics } from "../lib/analytics";
import { trace, updateTraceGating } from "../lib/trace";
import { api } from "../utils/apiClient";

// Re-export types for backward compatibility
export type { Client, UserWithRoles, RoleAssignment, ClientAssignment } from "@shared/types/rbac";

// Simple User type for backward compatibility
export interface User {
  id: string;
  email: string;
  name?: string;
  picture?: string;
}

/**
 * Full authentication state with RBAC support
 */
interface AuthState {
  user: UserWithRoles | null;
  currentClient: Client | null;
  isAuthenticated: boolean;
  isCheckingAuth: boolean;
  isSuperAdmin: boolean;
  userRole: string | null;
  permissions: string[];
}

// Create reactive state
const [authState, setAuthState] = createSignal<AuthState>({
  user: null,
  currentClient: null,
  isAuthenticated: false,
  isCheckingAuth: true,
  isSuperAdmin: false,
  userRole: null,
  permissions: [],
});

/**
 * Check authentication status with the API
 */
const checkAuthStatus = async (): Promise<UserWithRoles | null> => {
  trace.auth("checkAuthStatus started");
  try {
    const data = await api.get<{ authenticated?: boolean; user?: UserWithRoles }>("/api/auth/status");

    if (data.authenticated && data.user) {
      trace.auth("checkAuthStatus: authenticated", {
        email: data.user.email,
        isSuperAdmin: data.user.is_super_admin,
        clientCount: data.user.clients?.length ?? 0,
      });
      return data.user;
    }
    trace.auth("checkAuthStatus: response ok but not authenticated");
    return null;
  } catch (error: unknown) {
    trace.auth("checkAuthStatus error", { error: String(error) }, "error");
    analytics.error("auth_status_check_error", error);
    return null;
  }
};

/**
 * Get effective permissions for a specific client
 */
const getPermissionsForClient = (user: UserWithRoles, clientId: string | null): string[] => {
  // Super admin has all permissions
  if (user.is_super_admin) {
    return ["*"];
  }

  // Collect permissions from all applicable roles
  const permissions = new Set<string>();

  user.roles.forEach((roleAssignment: RoleAssignment) => {
    // Add permissions from system-wide roles (no client_id)
    if (!roleAssignment.client_id) {
      roleAssignment.role.permissions.forEach((p: string) => permissions.add(p));
    }
    // Add permissions from client-specific roles
    if (roleAssignment.client_id === clientId) {
      roleAssignment.role.permissions.forEach((p: string) => permissions.add(p));
    }
  });

  return Array.from(permissions);
};

/**
 * Check if user has a specific permission
 */
export const hasPermission = (permission: string, clientId?: string): boolean => {
  const state = authState();

  if (!state.isAuthenticated || !state.user) return false;
  if (state.isSuperAdmin) return true;

  const targetClientId = clientId || state.currentClient?.id || null;
  const permissions = getPermissionsForClient(state.user, targetClientId);

  // Check for wildcard permission
  if (permissions.includes("*")) return true;

  // Check for exact permission match
  if (permissions.includes(permission)) return true;

  // Check for category wildcard (e.g., "client:*" matches "client:read")
  const [category] = permission.split(":");
  if (permissions.includes(`${category}:*`)) return true;

  return false;
};

/**
 * Initialize authentication state
 */
export const initializeAuth = async () => {
  trace.auth("initializeAuth started");
  setAuthState((prev) => ({ ...prev, isCheckingAuth: true }));

  const user = await checkAuthStatus();

  if (user) {
    // Determine current client (primary or first available)
    const primaryClient = user.primary_client || user.clients[0];
    const currentClient = primaryClient?.client || null;
    const isSuperAdmin = user.is_super_admin;

    setAuthState({
      user,
      currentClient,
      isAuthenticated: true,
      isCheckingAuth: false,
      isSuperAdmin,
      userRole: primaryClient?.role.name || null,
      permissions: getPermissionsForClient(user, currentClient?.id || null),
    });

    trace.auth("initializeAuth: user set", {
      email: user.email,
      isSuperAdmin,
      client: currentClient?.name ?? "none",
      role: primaryClient?.role.name ?? "none",
    });

    // Update trace gating based on user
    updateTraceGating(user, (perm) => hasPermission(perm));

    // Identify user for analytics
    analytics.identify({
      userId: user.id,
      email: user.email,
      name: user.name ?? undefined,
      clientId: currentClient?.id,
      clientName: currentClient?.name,
      role: primaryClient?.role.name,
      isSuperAdmin,
    });
    analytics.track("auth_success", { clientCount: user.clients?.length || 0 });
    // Redirect first-time users (no accounts) to onboarding
    if (!user.is_super_admin && (!user.clients || user.clients.length === 0)) {
      trace.auth("initializeAuth: redirecting to onboarding (no clients)");
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/onboarding")) {
        window.location.href = "/onboarding";
      }
    }
  } else {
    trace.auth("initializeAuth: no user, setting unauthenticated");
    setAuthState({
      user: null,
      currentClient: null,
      isAuthenticated: false,
      isCheckingAuth: false,
      isSuperAdmin: false,
      userRole: null,
      permissions: [],
    });
    analytics.identify(null);
    updateTraceGating(null);
  }
};

/**
 * Refresh authentication status without resetting isCheckingAuth.
 *
 * Unlike initializeAuth(), this does NOT set isCheckingAuth to true.
 * Setting isCheckingAuth causes AuthWrapper to unmount its children
 * (showing a loading spinner), which destroys component state.
 * That's correct for initial page load, but during an in-page refresh
 * (e.g. after account creation in the onboarding wizard) it causes
 * the current component to remount and lose its local state.
 */
export const refreshAuth = async () => {
  trace.auth("refreshAuth started");
  const user = await checkAuthStatus();

  if (user) {
    const primaryClient = user.primary_client || user.clients[0];
    const currentClient = primaryClient?.client || null;
    const isSuperAdmin = user.is_super_admin;

    setAuthState({
      user,
      currentClient,
      isAuthenticated: true,
      isCheckingAuth: false,
      isSuperAdmin,
      userRole: primaryClient?.role.name || null,
      permissions: getPermissionsForClient(user, currentClient?.id || null),
    });

    trace.auth("refreshAuth: user set", {
      email: user.email,
      isSuperAdmin,
      client: currentClient?.name ?? "none",
    });

    updateTraceGating(user, (perm) => hasPermission(perm));

    analytics.identify({
      userId: user.id,
      email: user.email,
      name: user.name ?? undefined,
      clientId: currentClient?.id,
      clientName: currentClient?.name,
      role: primaryClient?.role.name,
      isSuperAdmin,
    });
  } else {
    trace.auth("refreshAuth: no user, setting unauthenticated");
    setAuthState({
      user: null,
      currentClient: null,
      isAuthenticated: false,
      isCheckingAuth: false,
      isSuperAdmin: false,
      userRole: null,
      permissions: [],
    });
    analytics.identify(null);
    updateTraceGating(null);
  }
};

/**
 * Logout the current user
 */
export const logout = async (): Promise<void> => {
  trace.auth("logout initiated");
  analytics.track("logout");
  try {
    await api.post("/api/auth/logout");
    trace.auth("logout: API call succeeded");
  } catch (error: unknown) {
    trace.auth("logout error", { error: String(error) }, "error");
    analytics.error("logout_error", error);
  } finally {
    setAuthState({
      user: null,
      currentClient: null,
      isAuthenticated: false,
      isCheckingAuth: false,
      isSuperAdmin: false,
      userRole: null,
      permissions: [],
    });
    analytics.identify(null);
    updateTraceGating(null);
  }
};

/**
 * Switch the current client context
 */
export const switchClient = async (clientId: string) => {
  const user = authState().user;
  if (!user) return;

  const clientAssignment = user.clients.find((c: ClientAssignment) => c.client.id === clientId);
  if (!clientAssignment) {
    analytics.error("client_switch_denied", new Error("Access denied"), { clientId });
    return;
  }

  // Update backend session
  try {
    await api.post("/api/auth/switch-client", { client_id: clientId });

    setAuthState((prev) => ({
      ...prev,
      currentClient: clientAssignment.client,
      userRole: clientAssignment.role.name,
      permissions: getPermissionsForClient(user, clientId),
    }));

    // Update analytics context with new client
    analytics.identify({
      userId: user.id,
      email: user.email,
      name: user.name ?? undefined,
      clientId: clientAssignment.client.id,
      clientName: clientAssignment.client.name,
      role: clientAssignment.role.name,
      isSuperAdmin: user.is_super_admin,
    });
    analytics.track("client_switched", {
      clientId: clientAssignment.client.id,
      clientName: clientAssignment.client.name,
      role: clientAssignment.role.name,
    });
  } catch (error: unknown) {
    analytics.error("client_switch_error", error, { clientId });
  }
};

// Initialize auth on module load
initializeAuth();

/**
 * Main auth hook for components
 *
 * Provides reactive access to auth state and helper methods
 */
export const useAuth = () => {
  return {
    // Reactive state accessors - must call authState() inside each accessor for reactivity
    user: () => authState().user,
    currentClient: () => authState().currentClient,
    isAuthenticated: () => authState().isAuthenticated,
    isCheckingAuth: () => authState().isCheckingAuth,
    isSuperAdmin: () => authState().isSuperAdmin,
    userRole: () => authState().userRole,
    permissions: () => authState().permissions,

    // Role check helpers
    isAdmin: () => authState().isSuperAdmin || authState().userRole === "Client Admin",
    isAirbrxAdmin: () => authState().isSuperAdmin,
    isClientAdmin: () => authState().userRole === "Client Admin",
    isClientUser: () => authState().userRole === "Client User",
    isViewer: () => authState().userRole === "Viewer",
    isClient: () => !authState().isSuperAdmin, // Legacy compatibility

    // Permission and client helpers
    hasPermission: (permission: string, clientId?: string) => hasPermission(permission, clientId),
    availableClients: () => authState().user?.clients || [],

    // Actions
    switchClient,
    refreshAuth,
    logout,
  };
};
